import React, { useEffect, useMemo, Suspense } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import map from 'lodash/map';

import RouteValidation from './route-validation';

import {
  ROOT_PATH,
  NOT_FOUND_PATH,
  ABOUT_PATH,
  JOIN_PATH,
  PARTNERSHIP_AND_LICENSING_PATH,
  SUPPORTING_CHARITIES_PATH,
  VOLUNTEER_CONTENT_PATH,
  JOIN_AS_EXPERT_PATH,
  EXPERT_PATH,
  TERM_OF_SERVICE_PATH,
  SIGN_IN_PATH,
  SIGN_UP_PATH,
  SEARCH_PATH,
  FAQ_PATH,
  GIFT_CARD_SITE_PATH,
  GIFT_CARD_SUCCESS_PATH,
  GIFT_CARD_CANCEL_PATH,
  USER_ACCOUNT_SETTINGS_PATH,
  GIFT_CARD_REDEEM_PATH,
  EXPERT_ACCOUNT_SETTINGS_PATH,
  CALLS_DASHBOARD_PATH,
  CALENDAR_CONNECTION_CALLBACK_PATH,
  EXPERT_SIGN_UP_PATH,
  CONFIRMATION_SIGN_UP_PATH,
  FORGOT_PASSWORD_PATH,
  RESET_PASSWORD_PATH,
  CALL_REQUEST_PATH,
  SITE_INVITATION_PATH,
  FEEDBACK_FOR_SITE_PATH,
  RESEND_CONFIRMATION_PATH,
  ZOOM_CONNECTION_CALLBACK_PATH,
  SIGN_IN_WITH_TOKEN_PATH,
  MFA_REVOCATION_PATH,
  ONBOARDING_PATH,
  PENDING_EXPERT_APPROVAL_PATH,
  CALL_REQUEST_INTERACTION_CALLBACK,
  USER_ACCOUNT_SETTINGS_CALLBACK_PATH,
  USER_CREDIT_TRANSACTION_CALLBACK,
  SESSIONS_SEARCH_PAGE_PATH,
  VERBOSE_EXPERT_PATH,
  VERBOSE_CALL_REQUEST_PATH,
  JOIN_SESSION_PATH,
  EVENTS_PATH,
  EVENT_DETAILS_PATH,
  MEMBER_PATH,
  ORGANIZATIONS_SEARCH_PATH,
  ORGANIZATION_PATH,
  FUNDRAISER_PUBLIC_PAGE_PATH,
  DONATION_PATH,
  DONATION_SUCCESS_PATH,
  DONATION_CANCEL_PATH,
  DONATION_PROCESS_PAYMENT_PATH,
} from 'constants/route-paths';
import {
  ADMIN_SIGN_IN_WITH_TOKEN_PATH,
  ADMIN_BLOCK_PREVIEW_PATH,
  ADMIN_BLOCK_LIBRARY_PREVIEW_PATH,
  ADMIN_CUSTOM_PAGE_SCREENSHOT_PREVIEW_PATH,
  ADMIN_ROOT_PATH,
  CUSTOM_PAGE_PATH,
} from 'constants/admin-route-paths';
import { GET_ROOT_SITE, GET_DESIGN_PATHS, ActionSuccessType } from 'constants/redux-actions';
import { designPathsSelector, rootSiteSelector } from 'selectors/root-site';
import siteActions from 'actions/site-actions';
import ContainerLayout from 'components/container-layout';
import HomePage from 'containers/home-page';
import SitePage from 'containers/site-page';
import CustomPage from 'containers/custom-page';
import SkeletonLayout from 'components/skeleton-layout';

const NotFound = React.lazy(() => import('containers/not-found'));
const About = React.lazy(() => import('containers/about'));
const JoinPage = React.lazy(() => import('containers/join-page'));
const JoinAsExpertPage = React.lazy(() => import('containers/join-as-expert'));
const VolunteerContent = React.lazy(() => import('containers/volunteer-content'));
const TermOfService = React.lazy(() => import('containers/term-of-service'));
const PartnershipAndLicensing = React.lazy(() => import('containers/partnership-and-licensing'));
const SupportingCharities = React.lazy(() => import('containers/supporting-charities'));
const FAQPage = React.lazy(() => import('containers/faq'));
const SearchPage = React.lazy(() => import('containers/search-page'));
const SessionSearchPage = React.lazy(() => import('containers/sessions-search-page'));
const OrganizationsSearchPage = React.lazy(() => import('containers/organizations-search-page'));
const OrganizationPage = React.lazy(() => import('containers/organization-page'));
const FundraiserPublicPage = React.lazy(() => import('containers/fundraiser-page'));
const DonationPage = React.lazy(() => import('containers/donation-page'));
const DonationSuccessPage = React.lazy(() => import('containers/donation-success'));
const DonationCancelPage = React.lazy(() => import('containers/donation-cancel'));
const DonationProcessPaymentPage = React.lazy(() => import('containers/donation-process-payment-page'));

// Authentication pages
const SignInPage = React.lazy(() => import('containers/sign-in'));
const SignUpPage = React.lazy(() => import('containers/sign-up'));
const ExpertSignUp = React.lazy(() => import('containers/expert-sign-up'));
const ConfirmationSignUp = React.lazy(() => import('containers/confirmation-sign-up'));
const ForgotPasswordPage = React.lazy(() => import('containers/forgot-password'));
const ResetPasswordPage = React.lazy(() => import('containers/reset-password'));
const ResendConfirmationPage = React.lazy(() => import('containers/resend-confirmation'));
const SignInWithToken = React.lazy(() => import('containers/sign-in-with-token'));
const MfaRevocation = React.lazy(() => import('containers/mfa-revocation'));

// Gift card pages
const GiftCardSitePage = React.lazy(() => import('containers/gift-card-site-page'));
const GiftCardSuccessPage = React.lazy(() => import('containers/gift-card-success'));
const GiftCardCancelPage = React.lazy(() => import('containers/gift-card-cancel'));
const GiftCardRedeemPage = React.lazy(() => import('containers/gift-card-redeem'));

// Event pages
const EventsPage = React.lazy(() => import('containers/events-page'));
const EventDetailsPage = React.lazy(() => import('containers/event-details-page'));

// Expert/User pages
const ExpertPage = React.lazy(() => import('containers/expert-page'));
const MemberPage = React.lazy(() => import('components/member-page'));

// Private routes (account/settings)
const AccountSettingsPage = React.lazy(() => import('containers/account-settings'));
const ExpertSettingsPage = React.lazy(() => import('containers/expert-settings'));
const CallsDashboardPage = React.lazy(() => import('containers/calls-dashboard'));
const CallRequestPage = React.lazy(() => import('containers/call-request-page'));
const Onboarding = React.lazy(() => import('containers/onboarding'));
const PendingExpertApproval = React.lazy(() => import('containers/pending-expert-approval'));
const JoinSession = React.lazy(() => import('containers/join-session'));

// Callback/interaction pages
const CalendarConnectionCallback = React.lazy(() => import('containers/calendar-connection-callback'));
const ZoomConnectionCallback = React.lazy(() => import('containers/zoom-connection-callback'));
const SiteInvitation = React.lazy(() => import('containers/site-invitation'));
const FeedbackForSitePage = React.lazy(() => import('containers/feedback-for-site'));
const CallRequestInteractionCallback = React.lazy(() => import('containers/call-request-interaction-callback'));
const UserAccountSettingsCallback = React.lazy(() => import('containers/user-account-settings-callback'));
const UserCreditTransactionCallback = React.lazy(() => import('containers/user-credit-transaction-callback'));

// Custom/Admin pages

const CustomPagePreview = React.lazy(() => import('containers/admin/custom-page-preview'));
const AdminSignInWithToken = React.lazy(() => import('containers/admin/sign-in-with-token'));
const ContentBlockPreview = React.lazy(() => import('containers/admin/content-block-preview'));
const BlockLibraryPreview = React.lazy(() => import('containers/admin/block-library-preview'));
const CustomPageScreenshotPreview = React.lazy(() => import('containers/admin/custom-page-screenshot-preview'));
const AdminRouteValidation = React.lazy(() => import('./admin-route-validation'));

const ROUTES = [
  {
    path: ROOT_PATH,
    exact: true,
    container: HomePage,
    title: 'Home',
  },
  {
    path: TERM_OF_SERVICE_PATH,
    exact: true,
    container: TermOfService,
    title: 'Term of service',
  },
  {
    path: CALL_REQUEST_PATH,
    exact: true,
    privateRoute: true,
    container: CallRequestPage,
    title: 'Call request',
  },
  {
    path: EXPERT_PATH,
    exact: true,
    container: ExpertPage,
    title: 'Expert',
  },
  {
    path: USER_ACCOUNT_SETTINGS_PATH,
    exact: true,
    privateRoute: true,
    container: AccountSettingsPage,
    title: 'Account settings',
  },
  {
    path: EXPERT_ACCOUNT_SETTINGS_PATH,
    exact: true,
    privateRoute: true,
    container: ExpertSettingsPage,
    title: 'Expert settings',
    expertOnly: true,
  },
  {
    path: CALLS_DASHBOARD_PATH,
    exact: true,
    privateRoute: true,
    container: CallsDashboardPage,
    title: 'Calls dashboard',
  },
  {
    path: FEEDBACK_FOR_SITE_PATH,
    exact: true,
    privateRoute: true,
    container: FeedbackForSitePage,
    title: 'Feedback',
  },
  {
    path: NOT_FOUND_PATH,
    exact: true,
    container: NotFound,
    title: 'Not found',
  },
  // FIXME: Temporarily hide download app page
  // {
  //   path: DOWNLOAD_APP_PATH,
  //   exact: true,
  //   container: DownloadApp,
  //   title: 'Download app',
  // }
  {
    path: SEARCH_PATH,
    exact: true,
    container: SearchPage,
    lockable: true,
    title: 'Searching experts',
  },
  {
    path: GIFT_CARD_SITE_PATH,
    exact: true,
    container: GiftCardSitePage,
    title: 'Gift cards',
  },
  {
    path: GIFT_CARD_SUCCESS_PATH,
    exact: true,
    container: GiftCardSuccessPage,
  },
  {
    path: GIFT_CARD_CANCEL_PATH,
    exact: true,
    container: GiftCardCancelPage,
  },
  {
    path: GIFT_CARD_REDEEM_PATH,
    exact: true,
    privateRoute: true,
    container: GiftCardRedeemPage,
    title: 'Redeeming giftcard',
  },
  {
    path: CALENDAR_CONNECTION_CALLBACK_PATH,
    exact: true,
    privateRoute: true,
    hideDetectingAuthenticatedUser: true,
    container: CalendarConnectionCallback,
    expertOnly: true,
  },
  {
    path: ZOOM_CONNECTION_CALLBACK_PATH,
    exact: true,
    privateRoute: true,
    hideDetectingAuthenticatedUser: true,
    container: ZoomConnectionCallback,
    expertOnly: true,
  },
  {
    path: CONFIRMATION_SIGN_UP_PATH,
    exact: true,
    hideDetectingAuthenticatedUser: true,
    container: ConfirmationSignUp,
  },
  {
    path: FORGOT_PASSWORD_PATH,
    exact: true,
    hideDetectingAuthenticatedUser: true,
    container: ForgotPasswordPage,
  },
  {
    path: RESET_PASSWORD_PATH,
    exact: true,
    hideDetectingAuthenticatedUser: true,
    container: ResetPasswordPage,
  },
  {
    path: SITE_INVITATION_PATH,
    exact: true,
    privateRoute: true,
    container: SiteInvitation,
  },
  {
    path: RESEND_CONFIRMATION_PATH,
    exact: true,
    container: ResendConfirmationPage,
  },
  {
    path: SIGN_IN_WITH_TOKEN_PATH,
    exact: true,
    hideDetectingAuthenticatedUser: true,
    container: SignInWithToken,
  },
  {
    path: MFA_REVOCATION_PATH,
    exact: true,
    hideDetectingAuthenticatedUser: true,
    container: MfaRevocation,
  },
  {
    path: ONBOARDING_PATH,
    exact: true,
    privateRoute: true,
    container: Onboarding,
    title: 'Onboarding',
  },
  {
    path: PENDING_EXPERT_APPROVAL_PATH,
    exact: true,
    privateRoute: true,
    container: PendingExpertApproval,
    title: 'Expert onboarding',
    expertOnly: true,
  },
  {
    path: CALL_REQUEST_INTERACTION_CALLBACK,
    exact: true,
    hideDetectingAuthenticatedUser: true,
    container: CallRequestInteractionCallback,
  },
  {
    path: USER_ACCOUNT_SETTINGS_CALLBACK_PATH,
    exact: true,
    hideDetectingAuthenticatedUser: true,
    container: UserAccountSettingsCallback,
  },
  {
    path: USER_CREDIT_TRANSACTION_CALLBACK,
    exact: true,
    hideDetectingAuthenticatedUser: true,
    container: UserCreditTransactionCallback,
  },
  {
    path: EVENT_DETAILS_PATH,
    exact: true,
    container: EventDetailsPage,
  },
  {
    path: EVENTS_PATH,
    exact: true,
    container: EventsPage,
  },
  {
    path: MEMBER_PATH,
    exact: true,
    container: MemberPage,
  },
  {
    path: ORGANIZATIONS_SEARCH_PATH,
    exact: true,
    container: OrganizationsSearchPage,
    title: 'Organizations',
  },
  {
    path: ORGANIZATION_PATH,
    exact: true,
    container: OrganizationPage,
    title: 'Organization',
  },
  {
    path: SESSIONS_SEARCH_PAGE_PATH,
    exact: true,
    container: SessionSearchPage,
  },
  {
    path: JOIN_SESSION_PATH,
    exact: true,
    privateRoute: true,
    container: JoinSession,
  },
  {
    path: DONATION_PATH,
    exact: true,
    container: DonationPage,
    title: 'Donation',
  },
  {
    path: DONATION_PROCESS_PAYMENT_PATH,
    exact: true,
    container: DonationProcessPaymentPage,
    title: 'Donation Process Payment',
  },
  {
    path: DONATION_SUCCESS_PATH,
    exact: true,
    container: DonationSuccessPage,
    title: 'Donation Success',
  },
  {
    path: DONATION_CANCEL_PATH,
    exact: true,
    container: DonationCancelPage,
    title: 'Donation Cancelled',
  },
  {
    path: FUNDRAISER_PUBLIC_PAGE_PATH,
    exact: true,
    container: FundraiserPublicPage,
    title: 'Fundraiser',
  },
];

const OVERRIDABLE_ROUTES = [
  {
    path: VOLUNTEER_CONTENT_PATH,
    exact: true,
    container: VolunteerContent,
    title: 'Volunteer content',
  },
  {
    path: JOIN_AS_EXPERT_PATH,
    exact: true,
    container: JoinAsExpertPage,
    title: 'Join as expert',
  },
  {
    path: JOIN_PATH,
    exact: true,
    container: JoinPage,
    title: 'Join as member',
  },
  {
    path: ABOUT_PATH,
    exact: true,
    container: About,
    title: 'About',
  },
  {
    path: PARTNERSHIP_AND_LICENSING_PATH,
    exact: true,
    container: PartnershipAndLicensing,
    title: 'Partnership and licensing',
  },
  {
    path: SUPPORTING_CHARITIES_PATH,
    exact: true,
    container: SupportingCharities,
    title: 'Supporting charities',
  },
  {
    path: FAQ_PATH,
    exact: true,
    container: FAQPage,
    title: 'Frequently asked questions',
  },
  {
    path: SIGN_UP_PATH,
    exact: true,
    container: SignUpPage,
    title: 'Sign up',
  },
  {
    path: SIGN_IN_PATH,
    exact: true,
    container: SignInPage,
    title: 'Sign in',
  },
  {
    path: EXPERT_SIGN_UP_PATH,
    exact: true,
    container: ExpertSignUp,
    title: 'Expert sign up',
  },
];

const requiredSiteLoading = ![
  ADMIN_BLOCK_PREVIEW_PATH,
  ADMIN_BLOCK_LIBRARY_PREVIEW_PATH,
  ADMIN_CUSTOM_PAGE_SCREENSHOT_PREVIEW_PATH,
].includes(window.location.pathname);

const Router = () => {
  const dispatch = useDispatch();

  const { loading, childrenSites } = useSelector(rootSiteSelector);
  const { paths, loading: customPathsLoading } = useSelector(designPathsSelector);

  useEffect(() => {
    if (requiredSiteLoading) {
      const handlePreloadedData = () => {
        const preloadedData = window.__PRELOADED_API_DATA__;

        if (preloadedData?.sites) {
          dispatch({ type: ActionSuccessType(GET_ROOT_SITE), payload: { data: preloadedData.sites } });
        } else {
          if (!window.__PRELOAD_INITIATED__) {
            dispatch(siteActions.getRootSite());
          }
        }

        if (preloadedData?.designPaths) {
          dispatch({ type: ActionSuccessType(GET_DESIGN_PATHS), payload: { data: preloadedData.designPaths } });
        } else {
          if (!window.__PRELOAD_INITIATED__) {
            dispatch(siteActions.getDesignPaths());
          }
        }
      };

      handlePreloadedData();

      window.addEventListener('preloadedDataReady', handlePreloadedData);

      return () => {
        window.removeEventListener('preloadedDataReady', handlePreloadedData);
      };
    }
  }, [dispatch]);

  const childSitePaths = useMemo(() => map(childrenSites, ({ slug }) => `/:siteSlug(${slug})`), [childrenSites]);

  return requiredSiteLoading && (loading || customPathsLoading) ? (
    <SkeletonLayout />
  ) : (
    <Switch>
      <Redirect from={VERBOSE_CALL_REQUEST_PATH} to={CALL_REQUEST_PATH} />
      <Redirect from={VERBOSE_EXPERT_PATH} to={EXPERT_PATH} />

      <Route
        path={ADMIN_SIGN_IN_WITH_TOKEN_PATH}
        exact
        render={() => (
          <Suspense fallback={<SkeletonLayout />}>
            <AdminSignInWithToken />
          </Suspense>
        )}
      />
      <Route
        path={ADMIN_BLOCK_PREVIEW_PATH}
        render={() => (
          <Suspense fallback={<SkeletonLayout />}>
            <ContentBlockPreview />
          </Suspense>
        )}
      />
      <Route
        path={ADMIN_BLOCK_LIBRARY_PREVIEW_PATH}
        render={() => (
          <Suspense fallback={<SkeletonLayout />}>
            <BlockLibraryPreview />
          </Suspense>
        )}
      />
      <Route
        path={ADMIN_CUSTOM_PAGE_SCREENSHOT_PREVIEW_PATH}
        render={() => (
          <Suspense fallback={<SkeletonLayout />}>
            <CustomPageScreenshotPreview />
          </Suspense>
        )}
      />
      <Route
        path={CUSTOM_PAGE_PATH}
        exact
        render={() => (
          <Suspense fallback={<SkeletonLayout />}>
            <ContainerLayout container={CustomPagePreview} />
          </Suspense>
        )}
      />

      <Route
        path={ADMIN_ROOT_PATH}
        render={() => (
          <Suspense fallback={<SkeletonLayout />}>
            <AdminRouteValidation />
          </Suspense>
        )}
      />

      {map(ROUTES, ({ path, container, ...routeConfig }) => (
        <Route
          {...routeConfig}
          key={path}
          path={path}
          render={() => (
            <Suspense fallback={null}>
              <RouteValidation container={container} {...routeConfig} />
            </Suspense>
          )}
        />
      ))}

      {map(childSitePaths, (childSitePath) => (
        <Route
          exact
          key={childSitePath}
          path={childSitePath}
          render={() => (
            <Suspense fallback={null}>
              <RouteValidation container={SitePage} title='Home' />
            </Suspense>
          )}
        />
      ))}

      {map(paths, (customPath) => (
        <Route
          exact
          key={customPath.customPageId}
          path={customPath.pathPattern}
          render={() => (
            <Suspense fallback={<SkeletonLayout />}>
              <RouteValidation
                container={() => <CustomPage {...customPath} />}
                customPageId={customPath.customPageId}
                staticJsonUrl={customPath.staticJsonUrl}
              />
            </Suspense>
          )}
        />
      ))}

      {map(OVERRIDABLE_ROUTES, ({ path, container, ...routeConfig }) => (
        <Route
          {...routeConfig}
          key={path}
          path={path}
          render={() => (
            <Suspense fallback={<SkeletonLayout />}>
              <RouteValidation container={container} {...routeConfig} />
            </Suspense>
          )}
        />
      ))}

      <Route render={() => <Redirect to={NOT_FOUND_PATH} />} />
    </Switch>
  );
};

export default Router;
